import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ConsentService } from '@core/services';
import { OsButtonDirective, OsSheetComponent, OsSwitchComponent } from '@shared/ui';
import { filter, map } from 'rxjs';

/**
 * Onde o banner não aparece: o portal do cliente (é do cliente do escritório),
 * o celular dentro da landing e o app logado (cobriria a barra de baixo; quem
 * entra passou pelo site ou pela tela de entrada, onde a pergunta aparece).
 * Sem resposta, a análise fica desligada: nada sai sem o "sim".
 */
const HIDDEN = /^\/(p|app)\/|^\/onboarding|[?&]embed=1/;

/**
 * Pergunta de cookies (LGPD), no rodapé da tela, até a pessoa escolher.
 * "Escolher" abre as preferências, que também abrem pelo rodapé do site.
 */
@Component({
	selector: 'os-consent-banner',
	imports: [RouterLink, OsButtonDirective, OsSheetComponent, OsSwitchComponent],
	template: `
		@if (showBanner()) {
			<section class="banner os-noprint" role="region" aria-label="Cookies">
				<p>
					Usamos cookies essenciais para o ObraSync funcionar. Com a sua permissão, também medimos o uso do site para
					melhorar o produto.
					<a routerLink="/cookies">Política de cookies</a>
				</p>
				<div class="banner__actions">
					<button (click)="consent.openPreferences()" osButton="ghost" size="sm" type="button">Escolher</button>
					<button (click)="consent.onlyEssential()" osButton="secondary" size="sm" type="button">Só essenciais</button>
					<button (click)="consent.acceptAll()" osButton size="sm" type="button">Aceitar</button>
				</div>
			</section>
		}

		<os-sheet
			[open]="consent.preferencesOpen()"
			(closed)="consent.preferencesOpen.set(false)"
			heading="Preferências de cookies"
			subtitle="Você muda quando quiser, pelo rodapé do site."
			size="sm"
			position="auto-center"
		>
			<div class="prefs">
				<label class="prefs__row" for="consent-essential">
					<span>
						<b>Essenciais</b>
						<span>Manter você conectado, lembrar o tema e guardar o registro feito sem sinal. Sempre ligados.</span>
					</span>
					<os-switch [value]="true" [disabled]="true" inputId="consent-essential" />
				</label>
				<label class="prefs__row" for="consent-analytics">
					<span>
						<b>Medir o uso</b>
						<span>
							Google Analytics, para saber quais páginas e botões ajudam de verdade. Desligado até você permitir.
						</span>
					</span>
					<os-switch [value]="analytics()" (valueChange)="analytics.set($event)" inputId="consent-analytics" />
				</label>
			</div>
			<ng-container footer>
				<button (click)="consent.onlyEssential()" osButton="ghost" type="button">Só essenciais</button>
				<button (click)="consent.save({ analytics: analytics() })" osButton type="button">Salvar</button>
			</ng-container>
		</os-sheet>
	`,
	styles: `
		.banner {
			position: fixed;
			z-index: var(--os-z-toast);
			bottom: 24px;
			left: 24px;
			display: flex;
			flex-direction: column;
			gap: var(--os-space-3);
			max-width: 440px;
			padding: 16px 18px;
			border: 1px solid var(--os-border);
			border-radius: var(--os-radius);
			background: var(--os-surface);
			box-shadow: var(--os-shadow-overlay);

			p {
				margin: 0;
				color: var(--os-ink-soft);
				font-size: var(--os-fs-13);
				line-height: var(--os-lh-body);
			}

			a {
				color: var(--os-accent-strong);
				font-weight: 600;
			}
		}

		.banner__actions {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			gap: var(--os-space-2);
		}

		.prefs {
			display: flex;
			flex-direction: column;
		}

		.prefs__row {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: var(--os-space-4);
			padding: var(--os-space-4) 0;

			& + & {
				border-top: 1px solid var(--os-border);
			}

			> span {
				display: flex;
				flex-direction: column;
				gap: 2px;
			}

			b {
				color: var(--os-ink);
			}

			span span {
				color: var(--os-ink-muted);
				font-size: var(--os-fs-13);
			}

			os-switch {
				flex: none;
				margin-right: 4px;
			}
		}

		@media (max-width: 760px) {
			.banner {
				right: 12px;
				bottom: 12px;
				left: 12px;
				max-width: none;
			}
		}

		@media print {
			.banner {
				display: none;
			}
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentBannerComponent {
	readonly consent = inject(ConsentService);
	private readonly router = inject(Router);

	private readonly url = toSignal(
		this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map((event) => event.urlAfterRedirects)
		),
		{ initialValue: this.router.url }
	);

	/**
	 * Sai pronto no HTML do servidor, para quem chega pela primeira vez ver na
	 * hora. Quem já respondeu nem chega a ver: o script do index.html marca
	 * `os-consent-done` no <html> antes do desenho e o _base.scss esconde.
	 */
	readonly showBanner = computed(() => !this.consent.decided() && !HIDDEN.test(this.url()));

	/** A escolha na tela de preferências, antes de salvar. */
	readonly analytics = signal(false);

	constructor() {
		effect(() => {
			if (this.consent.preferencesOpen()) untracked(() => this.analytics.set(this.consent.analytics()));
		});
	}
}
